import { useState } from 'react';
import styled from 'styled-components';
import { addAlpha } from '@algoan/deep-dive/theme';
import { useClickAwayListener } from '@algoan/dashboard/utils';
import { ArrowIcon } from '../icons';

export interface DropdownItem {
  label: string;
  key: string;
}

type Variant = 'primary' | 'secondary' | 'tertiary';

export interface SelectDropdownProps {
  className?: string;
  itemRenderer?: (option: DropdownItem) => React.ReactNode;
  selectedItemRenderer?: (option: DropdownItem) => React.ReactNode;
  onClick: (option: DropdownItem) => void;
  options: DropdownItem[];
  placeholder?: string;
  testAttribute?: string;
  variant?: Variant;
  value?: DropdownItem;
}

export const SelectDropdown: React.FC<SelectDropdownProps> = ({
  className,
  itemRenderer,
  selectedItemRenderer,
  onClick,
  options,
  placeholder = '...',
  testAttribute,
  value,
  variant = 'primary',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleDropdown = (): void => setIsOpen(!isOpen);
  const dropdownContainerRef = useClickAwayListener<HTMLDivElement>(() => {
    setIsOpen(false);
  });
  return (
    <S.DropdownContainer
      ref={dropdownContainerRef}
      data-testid={testAttribute}
      onClick={() => toggleDropdown()}
      variant={variant}
      className={className}
      data-is-selected={isOpen}
    >
      <S.DropdownSelected variant={variant} isPlaceholder={!value} isOpen={isOpen}>
        <S.DropdownSelectedItem>
          {selectedItemRenderer && value ? selectedItemRenderer(value) : (value?.label ?? placeholder)}
        </S.DropdownSelectedItem>
        <S.ArrowContainer variant={variant}>
          <S.ArrowIcon
            size={variant === 'tertiary' ? 'medium' : 'large'}
            direction={isOpen ? 'up' : 'down'}
            variant={variant}
          />
        </S.ArrowContainer>
      </S.DropdownSelected>

      <S.DropdownListContainer variant={variant} isOpen={isOpen}>
        <S.DropdownList variant={variant}>
          {options.map((option: DropdownItem) => (
            <S.ListItemContainer data-testid={option.key} onClick={() => onClick(option)} key={option.key}>
              {itemRenderer ? itemRenderer(option) : option.label}
            </S.ListItemContainer>
          ))}
        </S.DropdownList>
      </S.DropdownListContainer>
    </S.DropdownContainer>
  );
};

const DropdownContainer = styled.div<{
  variant: Variant;
}>`
  box-sizing: border-box;
  position: relative;

  ${({ variant }) => {
    switch (variant) {
      case 'secondary':
        return `
        height: 30px;
        width:245px;
        `;
      case 'tertiary':
        return `
         border: none;
        `;
      default:
        return ``;
    }
  }}
`;

export const S = {
  DropdownContainer,
  ArrowContainer: styled.div<{ variant: Variant }>`
    position: absolute;
    ${({ variant }) => {
      switch (variant) {
        case 'secondary':
          return `
            top: calc(50% - 7px);
            right: 11px
          `;
        case 'tertiary':
          return `
            top: calc(50% - 5px);
            right: 24px;
            `;
        default:
          return `
            top: calc(50% - 5px);
            right: 13px
            `;
      }
    }}
  `,
  DropdownSelected: styled.div<{ variant: Variant; isPlaceholder: boolean; isOpen: boolean }>`
    display: flex;
    align-items: center;
    font-weight: 400;
    color: ${(props) => (props.isPlaceholder ? props.theme.secondary.softBlue1 : props.theme.algoanBlue)};
    &:hover {
      cursor: pointer;
    }
    ${({ variant, theme, isOpen }) => {
      switch (variant) {
        case 'secondary':
          return `
          font-size: 12px;
          padding: 7px 15px;
          border: 1px solid ${theme.secondary.softBlue1};
          border-radius: 6px;
          &:hover {
            border: 1px solid ${theme.secondary.softBlue3};
          }
          `;
        case 'tertiary':
          return `
            padding-right: 50px;
            padding-left: 24px;
            color: ${theme.main.deepBlue};
            font-weight: 600;
            font-size: 14px;
            background-color: ${theme.secondary.chalk};
            border-radius: 30px;
            height: 38px;
            box-shadow: ${isOpen ? '0px 2px 13px 0px #00466b26' : 'none'};
            &:hover {
              box-shadow: 0px 2px 13px 0px #00466b26;
            }
            `;
        default:
          return `
              font-size: 14px;
              padding: 13px 15px;
              border: 1px solid ${theme.secondary.softBlue1};
              border-radius: 8px;
              `;
      }
    }}
  `,
  ListItemContainer: styled.div`
    display: flex;
    flex-direction: row;
    cursor: pointer;
    height: 34px;
    margin-bottom: 9px;
    padding-top: 2px;
    padding-left: 16px;
    padding-right: 16px;
    align-items: center;
    &:hover {
      background-color: ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)};
    }
  `,
  DropdownListContainer: styled.div<{ variant: Variant; isOpen: boolean }>`
    ${({ isOpen }) => (isOpen ? '' : 'display: none;')}
    position: absolute;
    width: 100%;
    z-index: 1;
    ${({ variant }) => {
      switch (variant) {
        case 'secondary':
          return `width: 245px`;
        case 'tertiary':
          return `
           width: 240px;
           position: absolute;
           right: 0;
          `;
      }
    }}
  `,
  DropdownSelectedItem: styled.div``,
  DropdownList: styled.ul<{ variant: Variant }>`
    padding: 0;
    margin-top: 8px;
    background: #ffffff;
    box-shadow: ${(props) => addAlpha(props.theme.algoanBlue, 0.15)};
    color: ${(props) => props.theme.algoanBlue};
    font-size: 12px;
    font-weight: 400;
    padding-top: 10px;
    padding-bottom: 1px;
    border-radius: 4px;
    ${({ variant }) => {
      switch (variant) {
        case 'primary':
          return `font-size: 14px;`;
        case 'secondary':
          return `font-size: 12px;`;
      }
    }}
  `,
  ArrowIcon: styled(ArrowIcon)<{ variant?: Variant }>`
    & path {
      stroke: ${({ theme }) => theme.secondary.softBlue1};
    }
    ${DropdownContainer}[data-is-selected='true'] & path, ${DropdownContainer}:hover & path {
      stroke: ${({ theme, variant }) =>
        variant === 'tertiary' ? theme.secondary.softBlue1 : theme.secondary.softBlue3};
    }
  `,
};
